import React, { useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, ChevronRight, Code2, Database, Cloud, Cpu, Layers, Terminal } from 'lucide-react';
import { stats } from '../../data/mock';

const HeroSection = () => {
  const heroRef = useRef(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add('active');
          }
        });
      },
      { threshold: 0.1 }
    );

    const elements = heroRef.current?.querySelectorAll('.reveal, .reveal-left, .reveal-right, .reveal-scale');
    elements?.forEach((el) => observer.observe(el));

    return () => observer.disconnect();
  }, []);

  // Floating code snippets for the abstract visual
  const codeSnippets = [
    { code: 'async deploy()', delay: '0s', top: '15%', left: '10%' },
    { code: '<Cloud />', delay: '0.5s', top: '25%', right: '15%' },
    { code: 'transform()', delay: '1s', bottom: '30%', left: '5%' },
    { code: '{ success }', delay: '1.5s', bottom: '20%', right: '10%' },
  ];

  // Floating tech icons
  const floatingIcons = [
    { Icon: Database, size: 'w-8 h-8', delay: '0s', top: '20%', left: '60%', color: 'text-orange-400' },
    { Icon: Cloud, size: 'w-10 h-10', delay: '0.3s', top: '35%', left: '75%', color: 'text-orange-500' },
    { Icon: Cpu, size: 'w-7 h-7', delay: '0.6s', top: '55%', left: '55%', color: 'text-orange-300' },
    { Icon: Layers, size: 'w-9 h-9', delay: '0.9s', top: '65%', left: '80%', color: 'text-orange-400' },
    { Icon: Code2, size: 'w-8 h-8', delay: '1.2s', top: '45%', left: '65%', color: 'text-orange-500' },
    { Icon: Terminal, size: 'w-6 h-6', delay: '1.5s', top: '75%', left: '60%', color: 'text-orange-300' },
  ];

  return (
    <section ref={heroRef} className="relative min-h-screen bg-gradient-to-br from-gray-50 via-white to-orange-50/30 overflow-hidden">
      {/* Animated Background Elements */}
      <div className="absolute inset-0 overflow-hidden">
        {/* Large Gradient Orbs */}
        <div className="hero-orb hero-orb-1"></div>
        <div className="hero-orb hero-orb-2"></div>
        <div className="hero-orb hero-orb-3"></div>
        
        {/* Animated Lines */}
        <svg className="absolute inset-0 w-full h-full opacity-20" xmlns="http://www.w3.org/2000/svg">
          <defs>
            <linearGradient id="lineGradient" x1="0%" y1="0%" x2="100%" y2="100%">
              <stop offset="0%" stopColor="#ff6b35" stopOpacity="0" />
              <stop offset="50%" stopColor="#ff6b35" stopOpacity="0.5" />
              <stop offset="100%" stopColor="#ff6b35" stopOpacity="0" />
            </linearGradient>
          </defs>
          <path className="animated-line" d="M-100,200 Q400,100 900,300 T1900,200" stroke="url(#lineGradient)" strokeWidth="2" fill="none" />
          <path className="animated-line-delayed" d="M-100,400 Q300,300 700,500 T1900,400" stroke="url(#lineGradient)" strokeWidth="1.5" fill="none" />
          <path className="animated-line-delayed-2" d="M-100,600 Q500,500 1000,700 T1900,600" stroke="url(#lineGradient)" strokeWidth="1" fill="none" />
        </svg>

        {/* Dot Grid Pattern */}
        <div className="absolute inset-0 dot-grid opacity-30"></div>
      </div>

      {/* Main Content */}
      <div className="relative max-w-7xl mx-auto px-6 lg:px-8 pt-28 pb-16">
        <div className="grid lg:grid-cols-2 gap-12 items-center min-h-[75vh]">
          
          {/* Left Content */}
          <div className="reveal-left z-10">
            {/* Badge */}
            <div className="inline-flex items-center gap-2 px-4 py-2 bg-white/90 backdrop-blur-md rounded-full shadow-lg shadow-orange-500/5 border border-orange-100/50 mb-8">
              <div className="w-2 h-2 rounded-full bg-green-500 animate-pulse"></div>
              <span className="text-sm font-medium text-gray-700">Trusted by 30+ Companies Worldwide</span>
            </div>

            {/* Headline */}
            <h1 className="text-5xl md:text-6xl lg:text-7xl font-bold text-gray-900 leading-[1.05] mb-6">
              We Build
              <span className="relative inline-block ml-3">
                <span className="relative z-10 bg-gradient-to-r from-[#ff6b35] via-[#ff8f5c] to-[#ff6b35] bg-clip-text text-transparent bg-[length:200%_auto] animate-gradient-x">Digital</span>
              </span>
              <br />
              <span className="inline-block mt-2">Solutions</span>
            </h1>

            {/* Description */}
            <p className="text-lg md:text-xl text-gray-600 mb-10 max-w-lg leading-relaxed">
              Expert SAP consultants, cloud architects, and full-stack developers ready to transform your business.
            </p>

            {/* CTA Buttons */}
            <div className="flex flex-wrap items-center gap-4 mb-12">
              <Link
                to="/contact"
                data-testid="hero-get-started-btn"
                className="group inline-flex items-center gap-3 bg-gradient-to-r from-[#ff6b35] to-[#ff8f5c] text-white px-8 py-4 rounded-full font-semibold text-lg shadow-xl shadow-[#ff6b35]/30 hover:shadow-[#ff6b35]/50 hover:scale-[1.02] transition-all duration-300"
              >
                Get Started
                <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform duration-300" />
              </Link>
              <Link
                to="/services"
                data-testid="hero-explore-services-btn"
                className="group inline-flex items-center gap-2 px-6 py-4 text-gray-700 font-semibold hover:text-[#ff6b35] transition-all duration-300"
              >
                Explore Services
                <ChevronRight className="w-5 h-5 group-hover:translate-x-1 transition-transform duration-300" />
              </Link>
            </div>

            {/* Stats Row */}
            <div className="flex items-center gap-6 md:gap-8 pt-8 border-t border-gray-200/60">
              <div className="stat-item">
                <div className="text-3xl md:text-4xl font-bold text-gray-900">{stats.projectsDone}+</div>
                <div className="text-sm text-gray-500">Projects</div>
              </div>
              <div className="w-px h-12 bg-gradient-to-b from-transparent via-gray-300 to-transparent"></div>
              <div className="stat-item">
                <div className="text-3xl md:text-4xl font-bold text-gray-900">{stats.customers}</div>
                <div className="text-sm text-gray-500">Clients</div>
              </div>
              <div className="w-px h-12 bg-gradient-to-b from-transparent via-gray-300 to-transparent"></div>
              <div className="stat-item">
                <div className="text-3xl md:text-4xl font-bold text-gray-900">{stats.resultsGuaranteed}%</div>
                <div className="text-sm text-gray-500">Success</div>
              </div>
            </div>
          </div>

          {/* Right Content - Abstract Tech Visual */}
          <div className="reveal-right relative h-[500px] lg:h-[600px] hidden lg:block">
            {/* Central Glowing Core */}
            <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2">
              {/* Outer Ring */}
              <div className="absolute -inset-32 border border-orange-200/30 rounded-full animate-spin-slow"></div>
              <div className="absolute -inset-24 border border-orange-300/40 rounded-full animate-spin-slow-reverse"></div>
              <div className="absolute -inset-16 border-2 border-dashed border-orange-400/30 rounded-full animate-spin-slower"></div>
              
              {/* Core Glow */}
              <div className="w-32 h-32 bg-gradient-to-br from-[#ff6b35] via-[#ff8f5c] to-[#ffb088] rounded-full blur-xl opacity-60 animate-pulse-glow"></div>
              <div className="absolute inset-0 w-32 h-32 bg-gradient-to-br from-[#ff6b35] to-[#ff8f5c] rounded-full flex items-center justify-center shadow-2xl shadow-orange-500/50">
                <Code2 className="w-12 h-12 text-white" />
              </div>
            </div>

            {/* Floating Tech Icons */}
            {floatingIcons.map(({ Icon, size, delay, color, ...pos }, i) => (
              <div
                key={i}
                className="absolute floating-icon"
                style={{ 
                  ...pos, 
                  animationDelay: delay,
                }}
              >
                <div className={`p-3 bg-white/90 backdrop-blur-sm rounded-2xl shadow-lg shadow-orange-500/10 border border-orange-100/50 hover:scale-110 hover:shadow-orange-500/20 transition-all duration-300 cursor-pointer`}>
                  <Icon className={`${size} ${color}`} />
                </div>
              </div>
            ))}

            {/* Floating Code Snippets */}
            {codeSnippets.map(({ code, delay, ...pos }, i) => (
              <div
                key={i}
                className="absolute floating-code"
                style={{ 
                  ...pos, 
                  animationDelay: delay,
                }}
              >
                <div className="px-4 py-2 bg-gray-900/95 backdrop-blur-sm rounded-lg shadow-xl border border-gray-700/50">
                  <code className="text-sm font-mono text-orange-400">{code}</code>
                </div>
              </div>
            ))}

            {/* Connecting Lines (SVG) */}
            <svg className="absolute inset-0 w-full h-full pointer-events-none" xmlns="http://www.w3.org/2000/svg">
              <defs>
                <linearGradient id="connectionGradient" x1="0%" y1="0%" x2="100%" y2="0%">
                  <stop offset="0%" stopColor="#ff6b35" stopOpacity="0" />
                  <stop offset="50%" stopColor="#ff6b35" stopOpacity="0.4" />
                  <stop offset="100%" stopColor="#ff6b35" stopOpacity="0" />
                </linearGradient>
              </defs>
              {/* Dynamic connection lines */}
              <line x1="50%" y1="50%" x2="60%" y2="20%" stroke="url(#connectionGradient)" strokeWidth="1" className="connection-line" />
              <line x1="50%" y1="50%" x2="75%" y2="35%" stroke="url(#connectionGradient)" strokeWidth="1" className="connection-line" style={{ animationDelay: '0.2s' }} />
              <line x1="50%" y1="50%" x2="55%" y2="55%" stroke="url(#connectionGradient)" strokeWidth="1" className="connection-line" style={{ animationDelay: '0.4s' }} />
              <line x1="50%" y1="50%" x2="80%" y2="65%" stroke="url(#connectionGradient)" strokeWidth="1" className="connection-line" style={{ animationDelay: '0.6s' }} />
              <line x1="50%" y1="50%" x2="65%" y2="45%" stroke="url(#connectionGradient)" strokeWidth="1" className="connection-line" style={{ animationDelay: '0.8s' }} />
            </svg>

            {/* Particle Effects */}
            <div className="absolute inset-0 overflow-hidden">
              {[...Array(12)].map((_, i) => (
                <div
                  key={i}
                  className="particle"
                  style={{
                    left: `${30 + Math.random() * 50}%`,
                    top: `${20 + Math.random() * 60}%`,
                    animationDelay: `${i * 0.3}s`,
                    animationDuration: `${3 + Math.random() * 2}s`,
                  }}
                />
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Bottom Wave */}
      <div className="absolute bottom-0 left-0 right-0">
        <svg viewBox="0 0 1440 120" fill="none" xmlns="http://www.w3.org/2000/svg" className="w-full">
          <path d="M0,60 C360,120 1080,0 1440,60 L1440,120 L0,120 Z" fill="white" fillOpacity="0.8"/>
          <path d="M0,80 C360,140 1080,20 1440,80 L1440,120 L0,120 Z" fill="white"/>
        </svg>
      </div>
    </section>
  );
};

export default HeroSection;
